import React from 'react'
import { PageShell } from './PageShell'
import { NudgeStatusBadge } from '../components/NudgeStatusBadge'
import { ChatTimeline } from '../components/ChatTimeline'
import { fetchNudgeQueueDetail } from '../../../lib/nudgeApi'
import { useDashboard } from '../DashboardContext'
import { formatDateTime, initialsFromName } from '../utils'
import { Skeleton, SkeletonListItem } from '../../../components/ui/Skeleton'
import type { NudgeQueueSummary, NudgeQueueDetail } from '../../../types/api'
import type { NudgeStatus } from '../../../types/enums'
import { ACTIVE_CONV_STATUSES, RECOVERED_STATUSES } from '../nudgeMetrics'

type ConvFilter = 'all' | 'sent' | 'active' | 'recovered' | 'failed'

type ContactThread = {
  contactId: string
  contactName: string
  contactEmail: string
  nudges: NudgeQueueSummary[]   // sorted ASC by sequenceNumber
  latest: NudgeQueueSummary     // highest sequenceNumber
  lastActivity: number
}

function matchesFilter(status: NudgeStatus, filter: ConvFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'sent') return status === 'SENT'
  if (filter === 'active') return ACTIVE_CONV_STATUSES.includes(status)
  // RETURNED is the post-recovered state once the owner logs the actual visit —
  // still a "recovered" outcome from the owner's perspective.
  if (filter === 'recovered') return RECOVERED_STATUSES.includes(status)
  if (filter === 'failed') return status === 'FAILED' || status === 'OPT_OUT' || status === 'EXHAUSTED'
  return false
}

function lastActivityTime(row: NudgeQueueSummary): number {
  return new Date(row.replyReceivedAt ?? row.sentAt ?? row.updatedAt).getTime()
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatDateTime(isoString)
}

function groupByContact(rows: NudgeQueueSummary[]): ContactThread[] {
  const map = new Map<string, NudgeQueueSummary[]>()
  for (const row of rows) {
    const existing = map.get(row.contactId) ?? []
    existing.push(row)
    map.set(row.contactId, existing)
  }
  return Array.from(map.values()).map(nudges => {
    // Display order: by sequenceNumber ASC (for the chat timeline + nudge count chip)
    const sorted = [...nudges].sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    // Status badge should reflect the chronologically latest row, with RECOVERED preferred
    const byRecent = [...nudges].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
    const recoveredRow = byRecent.find(n => RECOVERED_STATUSES.includes(n.status))
    const latest = recoveredRow ?? byRecent[0]
    return {
      contactId: latest.contactId,
      contactName: latest.contactName,
      contactEmail: latest.contactEmail,
      nudges: sorted,
      latest,
      lastActivity: Math.max(...sorted.map(n => lastActivityTime(n))),
    }
  })
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function MessagesPage({ isActive }: { isActive: boolean }) {
  const { queueRows, queueLoading } = useDashboard()
  const [search, setSearch] = React.useState('')
  const [filter, setFilter] = React.useState<ConvFilter>('all')
  const [selectedContactId, setSelectedContactId] = React.useState<string | null>(null)
  const hasAutoSelected = React.useRef(false)

  const threads = React.useMemo(
    () => groupByContact(queueRows).sort((a, b) => b.lastActivity - a.lastActivity),
    [queueRows],
  )

  React.useEffect(() => {
    if (hasAutoSelected.current || threads.length === 0) return
    setSelectedContactId(threads[0].contactId)
    hasAutoSelected.current = true
  }, [threads])

  const filteredThreads = React.useMemo(() => {
    let result = threads.filter(t => matchesFilter(t.latest.status, filter))
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      result = result.filter(t =>
        `${t.contactName} ${t.contactEmail}`.toLowerCase().includes(q)
      )
    }
    return [...result].sort((a, b) => b.lastActivity - a.lastActivity)
  }, [threads, filter, search])

  const counts = React.useMemo(() => ({
    all:       threads.length,
    sent:      threads.filter(t => t.latest.status === 'SENT').length,
    active:    threads.filter(t => ACTIVE_CONV_STATUSES.includes(t.latest.status)).length,
    recovered: threads.filter(t => RECOVERED_STATUSES.includes(t.latest.status)).length,
    failed:    threads.filter(t => t.latest.status === 'FAILED' || t.latest.status === 'OPT_OUT' || t.latest.status === 'EXHAUSTED').length,
  }), [threads])

  const selectedThread = React.useMemo(
    () => threads.find(t => t.contactId === selectedContactId) ?? null,
    [threads, selectedContactId],
  )

  const filterTabs: { key: ConvFilter; label: string; count: number }[] = [
    { key: 'all',       label: 'All',       count: counts.all       },
    { key: 'sent',      label: 'Sent',      count: counts.sent      },
    { key: 'active',    label: 'Active',    count: counts.active    },
    { key: 'recovered', label: 'Recovered', count: counts.recovered },
    { key: 'failed',    label: 'Failed',    count: counts.failed    },
  ]

  return (
    <PageShell id="page-messages" isActive={isActive}>
      <div style={{ display: 'flex', height: 'calc(100vh - 56px)' }}>
        {/* Left panel */}
        <div className="msg-list" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
            <div className="search-bar" style={{ maxWidth: '100%' }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                placeholder="Search conversations…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search conversations by name or email"
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 5,
              padding: '8px 10px',
              borderBottom: '1px solid var(--border)',
              flexWrap: 'wrap',
            }}
          >
            {filterTabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilter(tab.key)}
                style={{
                  padding: '3px 9px',
                  borderRadius: 99,
                  fontSize: 11,
                  fontFamily: "'DM Mono',monospace",
                  border: '1px solid var(--border)',
                  background: filter === tab.key ? 'var(--accent)' : 'var(--surface2)',
                  color: filter === tab.key ? '#fff' : 'var(--text2)',
                  cursor: 'pointer',
                  fontWeight: filter === tab.key ? 600 : 400,
                  lineHeight: 1.5,
                }}
              >
                {tab.label}
                {tab.count > 0 ? ` (${tab.count})` : ''}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }} aria-busy={queueLoading} aria-label="Conversations">
            {queueLoading ? (
              <>
                <SkeletonListItem />
                <SkeletonListItem />
                <SkeletonListItem />
              </>
            ) : filteredThreads.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  padding: '40px 16px',
                  textAlign: 'center',
                  color: 'var(--text2)',
                  fontSize: 13,
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 0.35 }}
                  aria-hidden="true"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span>
                  {threads.length === 0
                    ? 'No nudges sent yet. Threads will appear here once Nudge runs.'
                    : 'No threads match the current filter.'}
                </span>
              </div>
            ) : (
              filteredThreads.map(thread => {
                const isSelected = selectedContactId === thread.contactId
                return (
                  <div
                    key={thread.contactId}
                    className={`msg-item${isSelected ? ' active' : ''}`}
                    onClick={() => setSelectedContactId(thread.contactId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="msg-item-top">
                      <div className="msg-item-name">{thread.contactName}</div>
                      <div className="msg-item-time">
                        {relativeTime(new Date(thread.lastActivity).toISOString())}
                      </div>
                    </div>
                    <div className="msg-item-preview">{thread.contactEmail}</div>
                    <div className="msg-item-status">
                      <NudgeStatusBadge status={thread.latest.status} />
                      {thread.nudges.length > 1 && (
                        <span
                          className="pill pill-pending"
                          style={{ marginLeft: 4 }}
                          data-tooltip={`${thread.nudges.length} nudges sent to this contact`}
                        >
                          {thread.nudges.length} nudges
                        </span>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Right panel */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'var(--bg)',
          }}
        >
          {selectedThread ? (
            <InlineConversationPanel
              key={selectedThread.contactId}
              thread={selectedThread}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text2)',
                fontSize: 14,
              }}
            >
              Select a conversation to view details.
            </div>
          )}
        </div>
      </div>
    </PageShell>
  )
}

function InlineConversationPanel({ thread }: { thread: ContactThread }) {
  const { isFeatureAllowed, businessInfo } = useDashboard()
  const businessName = businessInfo?.businessName?.trim() || 'You'
  const aiGated =
    !isFeatureAllowed('NUDGE_REPLY_AI') &&
    thread.nudges.some(n => n.status === 'REPLY_RECEIVED')
  const [details, setDetails] = React.useState<NudgeQueueDetail[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [infoOpen, setInfoOpen] = React.useState(false)

  const nudgeIds = thread.nudges.map(n => n.id).join(',')

  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetails([])
    setInfoOpen(false)

    Promise.all(thread.nudges.map(n => fetchNudgeQueueDetail(n.id)))
      .then(results => { if (!cancelled) setDetails(results) })
      .catch(() => { if (!cancelled) setError('Failed to load conversation details.') })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudgeIds])

  const latest = thread.latest

  return (
    <>
      {/* Header */}
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--surface)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            className="avatar-circle"
            style={{ background: 'var(--blue-bg)', color: 'var(--blue)', width: 36, height: 36 }}
          >
            {initialsFromName(thread.contactName)}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>
              {thread.contactName}
            </div>
            <div
              style={{
                fontSize: 12,
                fontFamily: "'DM Mono',monospace",
                color: 'var(--text2)',
              }}
            >
              {thread.contactEmail}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <NudgeStatusBadge status={latest.status} />
          <button
            type="button"
            onClick={() => setInfoOpen(o => !o)}
            disabled={loading}
            aria-label="Thread info"
            aria-expanded={infoOpen}
            style={{
              width: 28,
              height: 28,
              border: `1px solid ${infoOpen ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 6,
              background: infoOpen ? 'var(--accent-bg)' : 'transparent',
              color: infoOpen ? 'var(--accent)' : 'var(--text2)',
              cursor: loading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading ? 0.4 : 1,
              padding: 0,
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        </div>
      </div>

      {/* Info panel */}
      {infoOpen && details.length > 0 ? (
        <div
          style={{
            display: 'grid',
            gap: 6,
            padding: '12px 20px',
            background: 'var(--surface)',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          {details.map(d => (
            <React.Fragment key={d.id}>
              {details.length > 1 ? (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'var(--text3)',
                    fontFamily: "'DM Mono',monospace",
                    letterSpacing: '0.06em',
                    marginTop: 4,
                  }}
                >
                  NUDGE #{d.sequenceNumber}
                </div>
              ) : null}
              <TimestampRow label="Created" value={d.createdAt} />
              {d.sentAt ? <TimestampRow label="Sent" value={d.sentAt} /> : null}
              {d.followUpSendAfter ? (
                <TimestampRow label="Next follow-up" value={d.followUpSendAfter} />
              ) : null}
              {d.replyReceivedAt ? (
                <TimestampRow label="Reply received" value={d.replyReceivedAt} />
              ) : null}
              {d.recoveredAt ? (
                <TimestampRow label="Recovered at" value={d.recoveredAt} />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      ) : null}

      {/* Chat body */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div
            aria-busy="true"
            aria-label="Loading conversation"
            style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <Skeleton height={80} borderRadius={10} />
            <Skeleton height={40} borderRadius={8} />
            <Skeleton height={60} borderRadius={10} />
            <Skeleton height={40} borderRadius={8} />
          </div>
        ) : error ? (
          <div style={{ padding: '20px 24px' }}>
            <div className="conv-alert failed">{error}</div>
          </div>
        ) : details.length > 0 ? (
          <ChatTimeline
            details={details}
            aiGated={aiGated}
            businessName={businessName}
            customerName={thread.contactName?.trim() || 'Customer'}
            variant="chat"
          />
        ) : null}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '10px 20px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-bd)',
            borderRadius: 10,
            padding: '7px 14px',
            fontSize: 12,
            color: 'var(--text2)',
          }}
        >
          <span style={{ color: 'var(--accent)', fontWeight: 500 }}>Read-only view</span>
          {' '}— this conversation is managed by Nudge AI. Customers reply directly to your inbox.
        </div>
      </div>
    </>
  )
}

function TimestampRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 10px',
        background: 'var(--surface2)',
        borderRadius: 6,
        border: '1px solid var(--border)',
      }}
    >
      <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono',monospace" }}>
        {label}
      </span>
      <span style={{ fontSize: 11, color: 'var(--text2)', fontFamily: "'DM Mono',monospace" }}>
        {formatDateTime(value)}
      </span>
    </div>
  )
}
